import type React from 'react';
import {Alert, Box, Stack, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import {ClickToEdit} from '~/components/ClickToEdit/ClickToEdit';
import {Tag} from '~/components/tag/Tag';
import type {Criterion, PlanContent} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {NumberCell} from './NumberCell';

const NAMES: Record<Criterion, string> = {
    R: 'Regulatory', A: 'Assurance of supply', Q: 'Quality', S: 'Service', C: 'Cost', I: 'Innovation',
};

/** Option appraisal (A25): RAQSCI weights, scores 1-5 per option; score = Σ(weight × score) ÷ Σweights × 20. */
export const AppraisalTool: React.FC<PlanProps> = ({plan, actions}) => {
    const {content, derived, reference, canEdit} = plan;
    const a = content.appraisal;
    const save = (next: PlanContent['appraisal']) => void actions.save('appraisal', next, 'Appraisal updated.');

    return (
        <Stack gap={1.5}>
            {!derived.appraisal.weightsOk && (
                <Alert severity="warning">{`Weights add up to ${derived.appraisal.weightTotal}%, not 100%.`}</Alert>
            )}
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label="Option appraisal">
                    <TableHead>
                        <TableRow>
                            <TableCell>Criterion</TableCell>
                            <TableCell align="right">Weight %</TableCell>
                            {a.options.map((o, i) => (
                                <TableCell key={i} align="right" sx={{minWidth: 150}}>
                                    <ClickToEdit text={o.name} readOnly={!canEdit}
                                                 typographyProps={{variant: 'body2', fontWeight: 600}}
                                                 onSave={name => name.trim() && save({...a, options: a.options.map((x, j) => (j === i ? {...x, name: name.trim()} : x))})}/>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reference.criteria.map(c => (
                            <TableRow key={c}>
                                <TableCell>{`${c} · ${NAMES[c]}`}</TableCell>
                                <TableCell align="right">
                                    <NumberCell value={a.weights[c]} min={0} max={100} disabled={!canEdit} label={`Weight of ${NAMES[c]}`}
                                                onCommit={v => save({...a, weights: {...a.weights, [c]: v}})}/>
                                </TableCell>
                                {a.options.map((o, i) => (
                                    <TableCell key={i} align="right">
                                        <NumberCell value={o.scores[c]} min={1} max={5} disabled={!canEdit}
                                                    label={`${o.name}: ${NAMES[c]} score`}
                                                    onCommit={v => save({...a, options: a.options.map((x, j) => (
                                                        j === i ? {...x, scores: {...x.scores, [c]: v}} : x))})}/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={{fontWeight: 600}}>Score /100</TableCell>
                            <TableCell/>
                            {derived.appraisal.scores.map((s, i) => (
                                <TableCell key={i} align="right" sx={{fontWeight: 600}}>
                                    {s.toFixed(0)}{i === derived.appraisal.preferred && <Tag size="small" label="Preferred" color="success" sx={{ml: 1}}/>}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>
        </Stack>
    );
};
